import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { useUser } from '../../contexts/UserContext'
import { API_CONFIG } from '../../config/apiConfig'
import { wgs84ToGcj02, wgs84ToBd09 } from '../../utils/coordinate'
import { showToast } from '../../utils/messageUtil'
// G39 移除 AMapLoader，改为调起高德 APP 导航

/**
 * 控车业务逻辑 Hook
 * 封装：车辆列表加载、设备状态查询、开锁/闭锁、寻车导航
 */
export function useCarControl() {
    const { userInfo } = useUser()
    const [vehicles, setVehicles] = useState([])
    const [currentVehicle, setCurrentVehicle] = useState(null)
    const [deviceStatus, setDeviceStatus] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [actionLoading, setActionLoading] = useState(false)
    // 地图选择弹窗：null=关闭，有值={ car: {wgs,gcj,bd09}, user: {...}|null }
    const [mapPicker, setMapPicker] = useState(null)

    /**
     * 加载可控车辆列表
     */
    const loadVehicles = useCallback(async () => {
        if (!userInfo || !userInfo.open_id) return
        setLoading(true)
        setError(null)
        try {
            const res = await axios.post(
                `${API_CONFIG.apiBaseUrl}/carApplication/getControllableVehicles`,
                {
                    apiToken: API_CONFIG.apiToken,
                    companyId: API_CONFIG.companyId,
                    tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
                    applicantId: userInfo.open_id
                },
                { headers: { 'Content-Type': 'application/json' } }
            )
            if (res.data.code === 200 && res.data.data && res.data.data.length > 0) {
                setVehicles(res.data.data)
                setCurrentVehicle(res.data.data[0])
                loadDeviceStatus(res.data.data[0].deviceId)
            } else {
                setVehicles([])
                setCurrentVehicle(null)
                setDeviceStatus(null)
            }
        } catch (err) {
            console.error('加载车辆列表失败:', err)
            setError('加载车辆列表失败，请稍后重试')
        } finally {
            setLoading(false)
        }
    }, [userInfo])

    /**
     * 加载设备状态（通过 getVehiclesStatus 获取，包含小电压、锁状态、在线状态等）
     */
    const loadDeviceStatus = useCallback(async (deviceId) => {
        if (!deviceId) return
        try {
            const res = await axios.post(
                `${API_CONFIG.apiBaseUrl}/carControl/getVehiclesStatus`,
                {
                    tokenId: API_CONFIG.apiToken,
                    companyId: API_CONFIG.companyId,
                    tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
                    devid: deviceId
                },
                { headers: { 'Content-Type': 'application/json' } }
            )
            if (res.data.code === 200) {
                let statusData = res.data.data.data
                if (typeof statusData === 'string') {
                    try {
                        statusData = JSON.parse(statusData)
                    } catch (e) {
                        console.error('解析设备状态失败:', e)
                        return
                    }
                }
                // 解析锁状态：兼容对象形式 { lf: 0, lr: 0, ... } 和简单数字 1/0
                let lockStatus = statusData?.lock
                // G29 修复：锁状态取反（全部=0→关锁，任一=1→已解锁）
                //   新逻辑：全部0→锁关（isLocked=true闭锁高亮），任一1→已解锁（isLocked=false已解锁高亮）
                if (lockStatus && typeof lockStatus === 'object' && !Array.isArray(lockStatus)) {
                    lockStatus = Object.values(lockStatus).some(v => v > 0) ? 0 : 1
                } else if (lockStatus === 1 || lockStatus === '1') {
                    lockStatus = 0
                } else {
                    lockStatus = 1
                }
                setDeviceStatus({ ...statusData, lock: lockStatus })
            }
        } catch (err) {
            console.error('加载设备状态失败:', err)
        }
    }, [])

    /**
     * 切换车辆
     */
    const switchVehicle = useCallback((vehicle) => {
        setCurrentVehicle(vehicle)
        setDeviceStatus(null)
        loadDeviceStatus(vehicle.deviceId)
    }, [loadDeviceStatus])

    /**
     * 轮询查询锁状态，确认是否解锁/闭锁成功
     * @param {string} deviceId 设备ID
     * @param {number} expectedLock 期望的 lock 值（0=已解锁, 1=已闭锁）
     * @param {number} maxMs 最大等待时间（毫秒，默认 15000）
     * @param {number} intervalMs 轮询间隔（毫秒，默认 1500）
     * @returns {Promise<boolean>} 是否在超时前确认到期望状态
     */
    const pollLockStatus = useCallback(async (deviceId, expectedLock, maxMs = 15000, intervalMs = 1500) => {
        const start = Date.now()
        let attempt = 0
        while (Date.now() - start < maxMs) {
            attempt++
            // 第一次立即查，后续等 interval
            if (attempt > 1) {
                await new Promise(r => setTimeout(r, intervalMs))
            }
            try {
                const res = await axios.post(
                    `${API_CONFIG.apiBaseUrl}/carControl/getVehiclesStatus`,
                    {
                        tokenId: API_CONFIG.apiToken,
                        companyId: API_CONFIG.companyId,
                        tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
                        devid: deviceId
                    },
                    { headers: { 'Content-Type': 'application/json' } }
                )
                if (res.data.code === 200) {
                    let statusData = res.data.data.data
                    if (typeof statusData === 'string') {
                        try {
                            statusData = JSON.parse(statusData)
                        } catch (e) {
                            console.error('[轮询] 解析设备状态失败:', e)
                            continue
                        }
                    }
                    // 与 loadDeviceStatus 相同的锁状态解析逻辑
                    let lockStatus = statusData?.lock
                    if (lockStatus && typeof lockStatus === 'object' && !Array.isArray(lockStatus)) {
                        lockStatus = Object.values(lockStatus).some(v => v > 0) ? 0 : 1
                    } else if (lockStatus === 1 || lockStatus === '1') {
                        lockStatus = 0
                    } else {
                        lockStatus = 1
                    }
                    console.log(`[轮询] 第${attempt}次，期望 lock=${expectedLock}，实际 lock=${lockStatus}`)
                    if (lockStatus === expectedLock) {
                        // 锁状态已符合期望，同步到 state
                        setDeviceStatus({ ...statusData, lock: lockStatus })
                        return true
                    }
                    // 不符合期望，继续轮询
                }
            } catch (err) {
                console.error('[轮询] 查询锁状态失败:', err)
            }
        }
        return false
    }, [])

    /**
     * 解锁（开锁）—— 乐观更新 + 轮询确认
     */
    const unlock = useCallback(async () => {
        if (!currentVehicle || !userInfo) return null
        setActionLoading(true)
        // ★ 乐观更新：立即将 UI 显示为已解锁（lock=0）
        setDeviceStatus(prev => prev ? { ...prev, lock: 0 } : prev)
        try {
            const phone = (userInfo.mobile || '').replace(/^\+86/, '')
            const res = await axios.post(
                `${API_CONFIG.apiBaseUrl}/carControl/feishuCarControl`,
                {
                    apiToken: API_CONFIG.apiToken,
                    companyId: API_CONFIG.companyId,
                    tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
                    plateNumber: currentVehicle.plateNumber,
                    deviceId: currentVehicle.deviceId,
                    phone: phone,
                    ctrl: 'unlock'
                },
                { headers: { 'Content-Type': 'application/json' } }
            )
            if (res.data.code === 200) {
                // ★ 接口无报错 → 15 秒内轮询确认锁状态
                const confirmed = await pollLockStatus(currentVehicle.deviceId, 0)
                if (!confirmed) {
                    // ★ 超时未变化 → 回滚 UI 为闭锁
                    console.warn('[解锁] 15秒内未确认到解锁状态，回滚UI')
                    setDeviceStatus(prev => prev ? { ...prev, lock: 1 } : prev)
                    return { code: 500, message: '解锁超时，请检查车辆状态' }
                }
                return { code: 200, message: '解锁成功' }
            } else {
                // ★ 接口报错 → 回滚 UI
                setDeviceStatus(prev => prev ? { ...prev, lock: 1 } : prev)
                return { code: 500, message: res.data.message || '解锁失败' }
            }
        } catch (err) {
            console.error('解锁失败:', err)
            // ★ 网络错误 → 回滚 UI
            setDeviceStatus(prev => prev ? { ...prev, lock: 1 } : prev)
            return { code: 500, message: '解锁请求失败，请稍后重试' }
        } finally {
            setActionLoading(false)
        }
    }, [currentVehicle, userInfo, pollLockStatus])

    /**
     * 闭锁 —— 乐观更新 + 轮询确认
     */
    const lock = useCallback(async () => {
        if (!currentVehicle || !userInfo) return null
        setActionLoading(true)
        // ★ 乐观更新：立即将 UI 显示为已闭锁（lock=1）
        setDeviceStatus(prev => prev ? { ...prev, lock: 1 } : prev)
        try {
            const phone = (userInfo.mobile || '').replace(/^\+86/, '')
            const res = await axios.post(
                `${API_CONFIG.apiBaseUrl}/carControl/feishuCarControl`,
                {
                    apiToken: API_CONFIG.apiToken,
                    companyId: API_CONFIG.companyId,
                    tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
                    plateNumber: currentVehicle.plateNumber,
                    deviceId: currentVehicle.deviceId,
                    phone: phone,
                    ctrl: 'lock'
                },
                { headers: { 'Content-Type': 'application/json' } }
            )
            if (res.data.code === 200) {
                // ★ 接口无报错 → 15 秒内轮询确认锁状态
                const confirmed = await pollLockStatus(currentVehicle.deviceId, 1)
                if (!confirmed) {
                    // ★ 超时未变化 → 回滚 UI 为已解锁
                    console.warn('[闭锁] 15秒内未确认到闭锁状态，回滚UI')
                    setDeviceStatus(prev => prev ? { ...prev, lock: 0 } : prev)
                    return { code: 500, message: '闭锁超时，请检查车辆状态' }
                }
                return { code: 200, message: '闭锁成功' }
            } else {
                // ★ 接口报错 → 回滚 UI
                setDeviceStatus(prev => prev ? { ...prev, lock: 0 } : prev)
                return { code: 500, message: res.data.message || '闭锁失败' }
            }
        } catch (err) {
            console.error('闭锁失败:', err)
            // ★ 网络错误 → 回滚 UI
            setDeviceStatus(prev => prev ? { ...prev, lock: 0 } : prev)
            return { code: 500, message: '闭锁请求失败，请稍后重试' }
        } finally {
            setActionLoading(false)
        }
    }, [currentVehicle, userInfo, pollLockStatus])

    /**
     * 寻车导航（通过车辆状态接口获取 GPS 位置）
     */
    const findCar = useCallback(async () => {
        if (!currentVehicle) return null
        // ★ PC 端不调定位，直接提示
        // 判定标准（保守，避免手机端误判）：
        // 1) UA 不含任何移动设备关键字，且 2) 设备不支持触控（maxTouchPoints=0），两者同时满足才算 PC
        // 不能用 innerWidth>=768 判断——横屏手机/折叠屏展开态/平板视口宽度也 >=768，会导致手机端误报
        const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
        const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(ua)
        const hasTouch = typeof window !== 'undefined' && ('ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0)
        if (!isMobileUA && !hasTouch) {
            return { code: 500, message: 'PC端不支持定位，请打开手机端使用该功能' }
        }
        setActionLoading(true)
        try {
            // 通过车辆状态接口获取 GPS 位置
            const res = await axios.post(
                `${API_CONFIG.apiBaseUrl}/carControl/getVehiclesStatus`,
                {
                    tokenId: API_CONFIG.apiToken,
                    companyId: API_CONFIG.companyId,
                    tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
                    devid: currentVehicle.deviceId
                },
                { headers: { 'Content-Type': 'application/json' } }
            )
            if (res.data.code !== 200) {
                return { code: 500, message: res.data.message || '获取车辆状态失败' }
            }
            // 提取 GPS 坐标（兼容 lat/latitude, lng/lon/longitude 多种字段名）
            console.log('[寻车] API 响应结构: code={}, data keys={}', res.data.code, Object.keys(res.data.data || {}));
            let statusData = res.data.data.data
            if (statusData === undefined) {
                console.warn('[寻车] res.data.data.data 是 undefined，尝试 res.data.data 作为 DataBody');
                statusData = res.data.data;
            }
            console.log('[寻车] GPS 数据:', statusData?.gps);
            if (typeof statusData === 'string') {
                try {
                    statusData = JSON.parse(statusData)
                } catch (e) {
                    console.error('解析设备状态失败:', e)
                    return
                }
            }
            const gps = statusData.gps
            if (!gps) {
                return { code: 500, message: '暂无车辆定位信息' }
            }
            const carLat = parseFloat(gps.lat )
            const carLng = parseFloat(gps.lng)

            if (!carLat || !carLng || isNaN(carLat) || isNaN(carLng)) {
                return { code: 500, message: '车辆定位数据异常' }
            }

            // ★ 车辆坐标：WGS-84（设备 GPS）→ 同时算 GCJ-02（高德/腾讯）和 BD-09（百度）
            const carGcj = wgs84ToGcj02(carLng, carLat)
            const carBd09 = wgs84ToBd09(carLng, carLat)

            // ★ 用户定位（浏览器 geolocation 也是 WGS-84），同样转出三套坐标
            let userPos = null
            try {
                const userPosition = await new Promise((resolve, reject) => {
                    if (!navigator.geolocation) {
                        reject(new Error('浏览器不支持定位功能'))
                        return
                    }
                    navigator.geolocation.getCurrentPosition(
                        (position) => resolve(position),
                        (err) => reject(err),
                        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
                    )
                })
                const rawUserLng = userPosition.coords.longitude
                const rawUserLat = userPosition.coords.latitude
                userPos = {
                    wgs: { lng: rawUserLng, lat: rawUserLat },
                    gcj: wgs84ToGcj02(rawUserLng, rawUserLat),
                    bd09: wgs84ToBd09(rawUserLng, rawUserLat)
                }
            } catch (geoErr) {
                console.warn('[寻车] 获取用户位置失败，使用仅车辆位置:', geoErr)
                // 移动端定位失败时不报错，继续跳转（不带起点）
            }

            // 打开地图选择弹窗（高德/百度/腾讯，各自对应坐标系）
            setMapPicker({
                car: { wgs: { lng: carLng, lat: carLat }, gcj: carGcj, bd09: carBd09 },
                user: userPos
            })
            return { code: 200, message: '寻车成功' }
        } catch (err) {
            console.error('寻车失败: err=', err, 'typeof=', typeof err, 'keys=', Object.keys(err || {}))
            // G36 修复：PositionError 用 code 属性，不用 message.includes('denied')
            if (err.code === 1 || err.message?.includes('denied')) {
                return { code: 500, message: '定位权限被拒绝，请在飞书中允许定位' }
            }
            if (err.code === 3) {
                return { code: 500, message: '定位超时，请到户外开阔处重试' }
            }
            if (err.code === 2) {
                return { code: 500, message: '定位失败，请检查网络/位置' }
            }
            if (err.message?.includes('不支持')) {
                return { code: 500, message: '当前环境不支持定位功能' }
            }
            const errDetail = typeof err === 'object' ? JSON.stringify({code: err.code, message: err.message, stack: err.stack?.slice(0, 100)}) : String(err)
            return { code: 500, message: '获取车辆位置失败: ' + errDetail }
        } finally {
            setActionLoading(false)
        }
    }, [currentVehicle])

    /**
     * 原生 scheme 调起 APP：scheme（如 amapuri:// / baidumap:// / qqmap://）直接唤起 APP；
     * 若 2.5s 内 APP 未调起（页面仍可见），仅提示未安装，不做 H5 兜底
     */
    const openAppWithFallback = (schemeUrl, mapName) => {
        console.log('[寻车] 尝试调起 APP:', schemeUrl)
        let appOpened = false
        const onVisibilityChange = () => {
            if (document.hidden || document.visibilityState === 'hidden') {
                appOpened = true
            }
        }
        document.addEventListener('visibilitychange', onVisibilityChange)

        const iframe = document.createElement('iframe')
        iframe.style.display = 'none'
        iframe.src = schemeUrl
        document.body.appendChild(iframe)

        setTimeout(() => {
            document.removeEventListener('visibilitychange', onVisibilityChange)
            if (!appOpened) {
                console.log('[寻车] APP 未调起（可能未安装）:', mapName)
                showToast(`未检测到${mapName}APP，请安装后使用`, 'warning')
            }
            iframe.remove()
        }, 2500)
    }

    /**
     * 高德地图导航（GCJ-02 坐标）——默认步行
     * scheme amapuri:// 直接唤起 APP；未安装仅提示
     * 注：iOS 上 scheme 唤起会弹一次"即将跳转第三方"系统确认，属正常现象
     */
    const openAmapApp = (mapData) => {
        const fromPart = mapData.user
            ? `&slat=${mapData.user.gcj.lat}&slon=${mapData.user.gcj.lng}&sname=我的位置`
            : ''
        // t=2 步行（0驾车 1公交 2步行 3骑行）
        const schemeUrl = `amapuri://route/plan/?dlat=${mapData.car.gcj.lat}&dlon=${mapData.car.gcj.lng}&dname=车辆位置&dev=0&t=2${fromPart}`
        openAppWithFallback(schemeUrl, '高德')
        setMapPicker(null)
    }

    /**
     * 百度地图导航（BD-09 坐标，coord_type=bd09ll）——默认步行
     * scheme baidumap:// 直接唤起 APP；未安装仅提示
     */
    const openBaiduApp = (mapData) => {
        const origin = mapData.user ? `${mapData.user.bd09.lat},${mapData.user.bd09.lng}` : ''
        const destination = `${mapData.car.bd09.lat},${mapData.car.bd09.lng}`
        const schemeUrl = `baidumap://map/direction?${origin ? `origin=${origin}&origin_coord_type=bd09ll&` : ''}destination=${destination}&coord_type=bd09ll&mode=walking&src=feishu_vehicle`
        openAppWithFallback(schemeUrl, '百度')
        setMapPicker(null)
    }

    /**
     * 腾讯地图导航（GCJ-02 坐标，coord_type=gcj02）——默认步行
     * URI 协议：坐标用 fromcoord/tocoord（lat,lng），名称用 fromname/toname 分开
     * scheme qqmap:// 直接唤起 APP；未安装仅提示
     */
    const openTencentApp = (mapData) => {
        const fromCoordPart = mapData.user
            ? `fromcoord=${mapData.user.gcj.lat},${mapData.user.gcj.lng}&fromname=我的位置&`
            : ''
        const baseParams = `type=walk&policy=0&coord_type=gcj02&${fromCoordPart}tocoord=${mapData.car.gcj.lat},${mapData.car.gcj.lng}&toname=车辆位置`
        // 原生 scheme（直接唤起 APP）
        const schemeUrl = `qqmap://map/routeplan?${baseParams}`
        openAppWithFallback(schemeUrl, '腾讯')
        setMapPicker(null)
    }

    /**
     * PC 端展示车辆位置（打开高德地图静态图/标记页面，不导航）
     * 高德静态地图：https://uri.amap.com/marker?position=经度,纬度&name=...
     */
    const showCarOnMap = (carLng, carLat) => {
        const url = `https://uri.amap.com/marker?position=${carLng},${carLat}&name=车辆位置&src=mypage&coordinate=gaode&callnative=0`
        console.log('[寻车] PC 展示地图:', url)
        window.open(url, '_blank')
    }

    // 初始化：加载车辆列表
    useEffect(() => {
        if (userInfo && userInfo.open_id) {
            loadVehicles()
        }
    }, [userInfo, loadVehicles])

    return {
        vehicles,
        currentVehicle,
        deviceStatus,
        loading,
        actionLoading,
        error,
        loadVehicles,
        switchVehicle,
        unlock,
        lock,
        findCar,
        mapPicker,
        closeMapPicker: () => setMapPicker(null),
        openAmapApp,
        openBaiduApp,
        openTencentApp
    }
}
